/**
 * StartPage — full-viewport workspace replacing the marketing landing once the
 * user clicks "Try with your product". Owns Step 1 (Load your context).
 *
 * Visual structure (from feedback):
 *   - Top bar: logo + 3-step progress stepper + "Back to home" exit
 *   - Step 1 heading with explicit "Step 1 · Load your context"
 *   - Two-column input zone:
 *       LEFT  — "Your product"  (upload OR description; use either or both)
 *       RIGHT — "The feature you're considering" (one paragraph)
 *   - Voice option below the columns
 *   - Single Begin CTA, centered focus
 *
 * Steps 2 and 3 are rendered by Phase2Conversation / Phase3Output respectively
 * — but the stepper at the top stays visible during all three so the user
 * always knows where they are.
 */
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, Lightbulb, Mic, Upload, X } from "lucide-react";

import { api } from "../lib/api";
import { useVoiceTranscription } from "../hooks/useVoiceTranscription";

const ACCEPTED =
  ".pdf,.docx,.txt,.md,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const STEPS = [
  { n: 1, label: "Load context" },
  { n: 2, label: "Answer questions" },
  { n: 3, label: "See the picture" },
];

export const Stepper = ({ activeStep = 1 }) => (
  <div className="flex items-center gap-2 md:gap-3" data-testid="experience-stepper">
    {STEPS.map((s, i) => {
      const active = activeStep === s.n;
      const done = activeStep > s.n;
      return (
        <div key={s.n} className="flex items-center gap-2 md:gap-3">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-medium ${
                active
                  ? "bg-[#C4521E] text-[#F9F7F3]"
                  : done
                  ? "border border-[#C4521E] text-[#C4521E]"
                  : "border border-[#DDD9CF] text-[#56534A]"
              }`}
            >
              {done ? "✓" : s.n}
            </span>
            <span
              className={`text-xs md:text-sm ${
                active ? "text-[#1A1814] font-medium" : "text-[#56534A]"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <span className="text-[#DDD9CF] text-sm">→</span>
          )}
        </div>
      );
    })}
  </div>
);

export const ExperienceTopBar = ({ activeStep, onExit, exitLabel = "Back to home" }) => (
  <div className="border-b border-[#DDD9CF] bg-[#F9F7F3] sticky top-0 z-20">
    <div className="max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto px-6 md:px-12 xl:px-16 py-4 flex items-center justify-between gap-4 flex-wrap">
      <span className="font-serif-display text-xl text-[#1A1814]" data-testid="exp-logo">
        SuperHumanly
      </span>
      <Stepper activeStep={activeStep} />
      <button
        type="button"
        onClick={onExit}
        className="text-sm text-[#56534A] hover:text-[#C4521E] transition-colors flex items-center gap-1.5"
        data-testid="exit-to-home"
      >
        <ArrowLeft size={14} strokeWidth={1.5} />
        {exitLabel}
      </button>
    </div>
  </div>
);

export const StartPage = ({ session, setSession, onBegin, onExit, busy, error }) => {
  const [typed, setTyped] = useState(session?.context_typed || "");
  const [featureIdea, setFeatureIdea] = useState(session?.feature_idea || "");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileRef = useRef(null);

  const voiceFeature = useVoiceTranscription("");
  const voiceProduct = useVoiceTranscription("");

  useEffect(() => {
    if (!voiceFeature.transcript) return;
    setFeatureIdea((prev) => {
      const trimmed = (prev || "").trim();
      const v = voiceFeature.transcript.trim();
      if (!v || trimmed.endsWith(v)) return prev;
      return trimmed ? `${trimmed} ${v}` : v;
    });
  }, [voiceFeature.transcript]);

  useEffect(() => {
    if (!voiceProduct.transcript) return;
    setTyped((prev) => {
      const trimmed = (prev || "").trim();
      const v = voiceProduct.transcript.trim();
      if (!v || trimmed.endsWith(v)) return prev;
      return trimmed ? `${trimmed} ${v}` : v;
    });
  }, [voiceProduct.transcript]);

  useEffect(() => {
    if (!session?.id) return;
    const t = setTimeout(() => {
      api
        .updateContext(session.id, { typed, feature_idea: featureIdea })
        .then(setSession)
        .catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [typed, featureIdea, session?.id, setSession]);

  const hasProductContext =
    (typed && typed.trim().length > 0) ||
    (session?.context_documents && session.context_documents.length > 0);
  const hasFeatureIdea = featureIdea && featureIdea.trim().length > 0;
  const canBegin = hasProductContext && hasFeatureIdea;

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError("");
    try {
      const updated = await api.uploadDoc(session.id, file);
      setSession(updated);
    } catch (e) {
      setUploadError(e?.response?.data?.detail || "Could not read this file.");
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (index) => {
    const updated = await api.removeDoc(session.id, index);
    setSession(updated);
  };

  return (
    <div className="min-h-screen bg-[#F9F7F3]" data-testid="start-page">
      <ExperienceTopBar activeStep={1} onExit={onExit} />

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto px-6 md:px-12 xl:px-16 py-12 md:py-16"
      >
        {/* Step 1 header */}
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C4521E] mb-4">
          Step 1 of 3
        </div>
        <h1 className="font-serif-display text-3xl md:text-4xl xl:text-5xl text-[#1A1814] tracking-tight leading-tight">
          Load your context.
        </h1>
        <p className="mt-4 text-[#56534A] text-base xl:text-lg max-w-2xl leading-relaxed">
          Give me your product and the feature you're considering. The more I know, the sharper my answers.
        </p>

        <div className="hr-ink my-10" />

        {/* Two-column input zone */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* LEFT — Your product */}
          <section className="border border-[#DDD9CF] bg-white rounded-md p-6 xl:p-7" data-testid="col-product">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1A1814] text-[#F9F7F3] text-[11px] font-medium">
                A
              </span>
              <h2 className="font-serif-display text-xl text-[#1A1814]">Your product</h2>
            </div>
            <p className="text-sm text-[#56534A] mb-5">
              Either upload a PRD, or describe in your own words. Both is even better.
            </p>

            {/* Upload */}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full border border-dashed border-[#DDD9CF] hover:border-[#C4521E] hover:bg-[#FBE4D8]/20 rounded-md p-4 flex items-center gap-3 text-left transition-colors"
              data-testid="upload-card"
            >
              <Upload size={18} strokeWidth={1.5} className="text-[#C4521E] shrink-0" />
              <div>
                <div className="text-sm font-medium text-[#1A1814]">Upload PRD or spec</div>
                <div className="text-xs text-[#56534A] mt-0.5">PDF, DOCX, or TXT — max 5 MB</div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPTED}
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="upload-input"
              />
            </button>
            {uploading && <p className="text-xs text-[#56534A] mt-2">Reading…</p>}
            {uploadError && <p className="text-xs text-[#C4521E] mt-2">{uploadError}</p>}

            {/* Uploaded files list */}
            {session?.context_documents?.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="uploaded-docs">
                {session.context_documents.map((d, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-xs border border-[#DDD9CF] rounded px-3 py-1.5 bg-[#F9F7F3]"
                  >
                    <span className="text-[#1A1814] flex items-center gap-2 truncate">
                      <FileText size={12} strokeWidth={1.5} className="text-[#56534A] shrink-0" />
                      <span className="truncate">{d.filename}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeDoc(i)}
                      className="text-[#56534A] hover:text-[#C4521E] shrink-0 ml-2"
                      data-testid={`remove-doc-${i}`}
                      aria-label={`Remove ${d.filename}`}
                    >
                      <X size={12} strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* "or" divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-[#DDD9CF]" />
              <span className="text-xs uppercase tracking-[0.18em] text-[#56534A]">or</span>
              <div className="flex-1 h-px bg-[#DDD9CF]" />
            </div>

            {/* Textarea */}
            <label className="text-xs uppercase tracking-[0.14em] text-[#56534A] mb-2 block">
              Describe your product
            </label>
            <textarea
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="e.g. A workforce-management SaaS used by ~200 retail store managers in NZ. They review weekly rosters, approve shift swaps, and report incidents."
              rows={5}
              className="w-full border border-[#DDD9CF] focus:border-[#C4521E] outline-none rounded-md p-3 text-sm text-[#1A1814] placeholder-[#56534A]/60 resize-y bg-white leading-relaxed transition-colors"
              data-testid="text-input"
            />

            {/* Voice for Card A */}
            <div className="mt-3 flex items-center gap-3" data-testid="voice-row-product">
              <button
                type="button"
                onClick={voiceProduct.toggle}
                disabled={!voiceProduct.supported}
                className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors shrink-0 ${
                  voiceProduct.listening
                    ? "bg-[#C4521E] border-[#C4521E] text-[#F9F7F3] mic-listening"
                    : "border-[#DDD9CF] text-[#C4521E] hover:bg-[#C4521E]/5"
                } ${voiceProduct.supported ? "" : "opacity-40 cursor-not-allowed"}`}
                data-testid="voice-toggle-product"
                aria-label={voiceProduct.listening ? "Stop recording" : "Speak product description"}
              >
                <Mic size={15} strokeWidth={1.5} />
              </button>
              <span className="text-xs text-[#56534A]">
                {voiceProduct.supported
                  ? voiceProduct.listening
                    ? "Listening… describe your product. Click again to stop."
                    : "Or speak it — appended to the field above."
                  : "Voice requires Chrome or Edge."}
              </span>
            </div>
          </section>

          {/* RIGHT — The feature */}
          <section className="border border-[#DDD9CF] bg-white rounded-md p-6 xl:p-7" data-testid="col-feature">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#C4521E] text-[#F9F7F3] text-[11px] font-medium">
                B
              </span>
              <h2 className="font-serif-display text-xl text-[#1A1814]">
                The feature you're considering
              </h2>
            </div>
            <p className="text-sm text-[#56534A] mb-5">
              One paragraph is enough. What does it do, who triggers it, and roughly how often.
            </p>

            <label className="text-xs uppercase tracking-[0.14em] text-[#56534A] mb-2 flex items-center gap-2">
              <Lightbulb size={12} strokeWidth={1.5} className="text-[#C4521E]" />
              Describe the AI feature
            </label>
            <textarea
              value={featureIdea}
              onChange={(e) => setFeatureIdea(e.target.value)}
              placeholder="e.g. When a store manager submits an incident report, the AI summarises the free-text narrative into a structured incident record and routes it to the right team. ~80 reports/day. A wrong category or routing costs ~$50 in rework."
              rows={9}
              className="w-full border border-[#DDD9CF] focus:border-[#C4521E] outline-none rounded-md p-3 text-sm text-[#1A1814] placeholder-[#56534A]/60 resize-y bg-white leading-relaxed transition-colors"
              data-testid="feature-input"
            />

            {/* Voice for Card B */}
            <div className="mt-4 flex items-center gap-3" data-testid="voice-row">
              <button
                type="button"
                onClick={voiceFeature.toggle}
                disabled={!voiceFeature.supported}
                className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors shrink-0 ${
                  voiceFeature.listening
                    ? "bg-[#C4521E] border-[#C4521E] text-[#F9F7F3] mic-listening"
                    : "border-[#DDD9CF] text-[#C4521E] hover:bg-[#C4521E]/5"
                } ${voiceFeature.supported ? "" : "opacity-40 cursor-not-allowed"}`}
                data-testid="voice-toggle"
                aria-label={voiceFeature.listening ? "Stop recording" : "Speak instead"}
              >
                <Mic size={15} strokeWidth={1.5} />
              </button>
              <span className="text-xs text-[#56534A]">
                {voiceFeature.supported
                  ? voiceFeature.listening
                    ? "Listening… speak the feature idea. Click again to stop."
                    : "Or speak it — appended to the field above."
                  : "Voice requires Chrome or Edge."}
              </span>
            </div>
          </section>
        </div>

        {/* Footer — Begin CTA */}
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={onBegin}
            disabled={!canBegin || busy}
            className="bg-[#C4521E] text-[#F9F7F3] px-8 py-3 font-medium tracking-wide hover:bg-[#a34418] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="begin-button"
          >
            {busy ? "Reading…" : "Begin Step 2"}
            <span aria-hidden="true">→</span>
          </button>
          {!canBegin && (
            <p className="text-xs text-[#56534A] italic" data-testid="begin-hint">
              {!hasProductContext && !hasFeatureIdea
                ? "Need both your product and the feature idea to continue."
                : !hasProductContext
                ? "Add your product on the left."
                : "Add the feature idea on the right."}
            </p>
          )}
        </div>

        {error && (
          <p className="mt-4 text-sm text-[#C4521E]" data-testid="begin-error">
            {error}
          </p>
        )}
      </motion.div>
    </div>
  );
};
